export function formatBrl(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    const n = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(n)) {
        return '—';
    }
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
}

export function formatDateTime(iso: string | null | undefined): string {
    if (!iso) {
        return '—';
    }
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function formatDate(iso: string | null | undefined): string {
    if (!iso) {
        return '—';
    }
    return new Date(iso).toLocaleDateString('pt-BR');
}

export function statusBadgeColor(color: string): 'success' | 'danger' | 'warning' | 'info' | 'secondary' {
    if (color === 'success') return 'success';
    if (color === 'danger') return 'danger';
    if (color === 'warning') return 'warning';
    if (color === 'info') return 'info';
    return 'secondary';
}

type ListBadgeTone = 'primary' | 'success' | 'danger' | 'warning' | 'info' | 'secondary' | 'amber' | 'emerald' | 'blue';

/** Badge pill da listagem — dark mode nativo (evita tokens claros do RizzUI Badge). */
export function listBadgeClass(tone: ListBadgeTone): string {
    const map: Record<ListBadgeTone, string> = {
        primary: 'bg-orange-100 text-orange-800 dark:bg-orange-950/50 dark:text-orange-300',
        success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
        danger: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300',
        warning: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
        info: 'bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300',
        secondary: 'bg-gray-100 text-gray-700 dark:bg-zinc-800 dark:text-zinc-300',
        amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
        emerald: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300',
        blue: 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300',
    };

    return `inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${map[tone]}`;
}

export type FieldTone = 'primary' | 'success' | 'warning' | 'info' | 'gray';

export function fieldBadgeClass(tone: FieldTone): string {
    const map: Record<FieldTone, string> = {
        primary: 'bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300',
        success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
        warning: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300',
        info: 'bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300',
        gray: 'bg-gray-100 text-gray-700 dark:bg-zinc-800 dark:text-zinc-300',
    };
    return `inline-flex max-w-full items-center rounded-md px-2 py-0.5 text-xs font-semibold ${map[tone]}`;
}

export function contractBadgeClass(label: string | null): string {
    if (label === 'Compra e Venda') {
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300';
    }
    if (label === 'Locação') {
        return 'bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300';
    }
    return 'bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-400';
}

const OWNER_AVATAR_PALETTE = ['#0EA5E9', '#22C55E', '#F59E0B', '#8B5CF6', '#F97316', '#14B8A6', '#EC4899', '#6366F1'];

export function ownerInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const fromParts = parts
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');

    if (fromParts) {
        return fromParts;
    }

    return name.charAt(0).toUpperCase() || '?';
}

/** Primeiro + último nome quando houver mais de `maxParts` palavras. */
export function shortOwnerName(name: string, maxParts = 2): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return '';
    }
    if (parts.length <= maxParts) {
        return parts.join(' ');
    }
    if (maxParts <= 1) {
        return parts[0];
    }

    return `${parts[0]} ${parts[parts.length - 1]}`;
}

/**
 * Nome curto para tabela: primeiro + sobrenome.
 * Se o nome completo tiver mais partes, acrescenta "…" (tooltip deve levar o nome inteiro).
 */
export function shortDisplayName(name: string | null | undefined): { short: string; full: string; truncated: boolean } {
    const full = (name ?? '').trim();
    if (!full) {
        return { short: '', full: '', truncated: false };
    }
    const short = shortOwnerName(full);
    const truncated = short !== full;

    return {
        short: truncated ? `${short}…` : short,
        full,
        truncated,
    };
}

export function ownerAvatarColor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i += 1) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return OWNER_AVATAR_PALETTE[Math.abs(hash) % OWNER_AVATAR_PALETTE.length];
}
